import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";

export type RangeSliderInputProps = {
  min: number;
  max: number;
  valueMin?: number;
  valueMax?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  formatLabel?: (val: number) => string;
  onChange: (range: { min: number; max: number }) => void;
};

const clamp = (val: number, min: number, max: number) => {
  if (isNaN(val)) return min;
  return Math.max(min, Math.min(max, val));
};

const quantize = (val: number, step: number, min: number) => {
  if (!step || step <= 0) return val;
  const rounded = Math.round((val - min) / step) * step + min;
  return rounded;
};

export const RangeSliderInput: React.FC<RangeSliderInputProps> = ({
  min,
  max,
  valueMin,
  valueMax,
  step = 1,
  disabled,
  className,
  formatLabel,
  onChange
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const activeHandleRef = useRef<"min" | "max" | null>(null);

  const safeMin = useMemo(
    () => clamp(typeof valueMin === "number" ? valueMin : min, min, max),
    [valueMin, min, max]
  );
  const safeMax = useMemo(
    () => clamp(typeof valueMax === "number" ? valueMax : max, min, max),
    [valueMax, min, max]
  );

  const totalSpan = Math.max(1, max - min);
  const leftPercent = ((safeMin - min) / totalSpan) * 100;
  const rightPercent = ((safeMax - min) / totalSpan) * 100;

  const getValueFromClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return undefined;
      const rect = track.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      const raw = min + ratio * (max - min);
      const q = quantize(raw, step, min);
      return clamp(q, min, max);
    },
    [min, max, step]
  );

  const handlePointerMove = useCallback(
    (clientX: number) => {
      if (!activeHandleRef.current || disabled) return;
      const val = getValueFromClientX(clientX);
      if (val === undefined) return;
      if (activeHandleRef.current === "min") {
        const nextMin = Math.min(val, safeMax);
        if (nextMin !== safeMin) {
          onChange({ min: nextMin, max: safeMax });
        }
      } else if (activeHandleRef.current === "max") {
        const nextMax = Math.max(val, safeMin);
        if (nextMax !== safeMax) {
          onChange({ min: safeMin, max: nextMax });
        }
      }
    },
    [disabled, getValueFromClientX, onChange, safeMax, safeMin]
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => handlePointerMove(e.clientX);
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches && e.touches.length > 0) {
        handlePointerMove(e.touches[0].clientX);
      }
    };
    const onUp = () => {
      activeHandleRef.current = null;
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [handlePointerMove]);

  const startDrag = (handle: "min" | "max") => {
    if (disabled) return;
    activeHandleRef.current = handle;
  };

  const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    const val = getValueFromClientX(e.clientX);
    if (val === undefined) return;
    // Move the nearest handle
    const distToMin = Math.abs(val - safeMin);
    const distToMax = Math.abs(val - safeMax);
    if (distToMin <= distToMax) {
      const nextMin = Math.min(val, safeMax);
      onChange({ min: nextMin, max: safeMax });
    } else {
      const nextMax = Math.max(val, safeMin);
      onChange({ min: safeMin, max: nextMax });
    }
  };

  const decrement = (handle: "min" | "max") => {
    if (disabled) return;
    if (handle === "min") {
      const nextMin = clamp(quantize(safeMin - step, step, min), min, safeMax);
      onChange({ min: nextMin, max: safeMax });
    } else {
      const nextMax = clamp(quantize(safeMax - step, step, min), safeMin, max);
      onChange({ min: safeMin, max: nextMax });
    }
  };
  const increment = (handle: "min" | "max") => {
    if (disabled) return;
    if (handle === "min") {
      const nextMin = clamp(quantize(safeMin + step, step, min), min, safeMax);
      onChange({ min: nextMin, max: safeMax });
    } else {
      const nextMax = clamp(quantize(safeMax + step, step, min), safeMin, max);
      onChange({ min: safeMin, max: nextMax });
    }
  };

  return (
    <div className={cn("flex w-full flex-col gap-2", className)}>
      <div
        ref={trackRef}
        className={cn(
          "relative z-0 flex h-6 items-center",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        )}
        onMouseDown={onTrackClick}
      >
        {/* Background Track */}
        <div className="absolute z-0 h-2 w-full rounded-full bg-gray-light-600 dark:bg-gray-dark-200" />
        {/* Selected Range Fill */}
        <div
          className="absolute z-0 mx-[1px] h-[6px] rounded-full bg-gradient-to-r from-white to-[#0070F3] dark:from-black dark:to-[#0070F3]"
          style={{
            left: `${leftPercent}%`,
            width: `${Math.max(0, rightPercent - leftPercent)}%`
          }}
        />
        {/* Min Handle */}
        <button
          type="button"
          aria-label="Minimum value"
          className={cn(
            "absolute z-10 h-3 w-3 -translate-x-1/2 rounded-full border border-black/50 bg-white dark:border-white/60",
            disabled ? "pointer-events-none" : "pointer-events-auto"
          )}
          style={{ left: `${leftPercent}%` }}
          onMouseDown={(e) => {
            e.stopPropagation();
            startDrag("min");
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            startDrag("min");
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
              e.preventDefault();
              decrement("min");
            } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
              e.preventDefault();
              increment("min");
            }
          }}
          tabIndex={0}
        />
        {/* Max Handle */}
        <button
          type="button"
          aria-label="Maximum value"
          className={cn(
            "absolute z-10 h-3 w-3 -translate-x-1/2 rounded-full border border-black/50 bg-white dark:border-white/60",
            disabled ? "pointer-events-none" : "pointer-events-auto"
          )}
          style={{ left: `${rightPercent}%` }}
          onMouseDown={(e) => {
            e.stopPropagation();
            startDrag("max");
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            startDrag("max");
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
              e.preventDefault();
              decrement("max");
            } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
              e.preventDefault();
              increment("max");
            }
          }}
          tabIndex={0}
        />
      </div>
      <div className="subtext-x-small relative z-0 flex flex-row items-center justify-between font-medium leading-4 text-gray-dark-2200">
        <span className="text-left">{formatLabel ? formatLabel(min) : min}</span>
        <span className="text-right">{formatLabel ? formatLabel(max) : max}</span>
      </div>
    </div>
  );
};

export default RangeSliderInput;
